import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IMAGE_CONFIGS, ImageUploadType } from '../../config/imageUploadConfig';
import { validateImageFile, ValidationIssue } from '../../utils/image-validation.util';
import { ImageEditorModalComponent } from './image-editor-modal/image-editor-modal.component';
import { readImageMetadata, ImageMetadata } from '../../utils/image-editor.util';
import { ChangeDetectorRef, inject } from '@angular/core';

export interface ValidImageResult {
  file: File;
  url: string;
  width: number;
  height: number;
}


export type ProcessedFileStatus = 
  | 'NEW_PENDING' 
  | 'NEW_ACCEPTED' 
  | 'NEW_REJECTED_FIXABLE' 
  | 'NEW_REJECTED_FATAL';

export interface ProcessedFile {
  file: File;
  status: ProcessedFileStatus;
  issues: ValidationIssue[];
  width?: number;
  height?: number;
  url?: string;
}

@Component({
  selector: 'app-image-uploader',
  standalone: true,
  imports: [CommonModule, ImageEditorModalComponent],
  templateUrl: './image-uploader.component.html',
})
export class ImageUploaderComponent {
  @Input() type!: ImageUploadType;
  @Input() multiple = false;
  @Input() existingImageUrl: string | null = null;
  
  @Output() filesSelected = new EventEmitter<ValidImageResult[]>();
  @Output() imageRemoved = new EventEmitter<void>();

  private cdr = inject(ChangeDetectorRef);

  isDragging = false;
  processedFiles: ProcessedFile[] = [];

  activeFileToEdit: ProcessedFile | null = null;
  activeFileMetadata: ImageMetadata | null = null;
  activeFileFixReason = '';

  private markStateChanged() {
    this.cdr.markForCheck();
  }

  get config() {
    return IMAGE_CONFIGS[this.type];
  }

  get previewUrl(): string | null {
    for (let index = this.processedFiles.length - 1; index >= 0; index -= 1) {
      const item = this.processedFiles[index];
      if (item.status === 'NEW_ACCEPTED' && item.url) return item.url;
    }
    return this.existingImageUrl;
  }

  get ratioLabel(): string {
    if (this.config.targetRatio === 1) return '1:1';
    if (Math.abs(this.config.targetRatio - 4) < 0.001) return '4:1';
    return `${this.config.targetRatio.toFixed(2)}:1`;
  }

  get acceptedFormatLabel(): string {
    return this.config.allowedFormats
      .map(format => format.replace('image/', '').toUpperCase())
      .join(', ');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.markStateChanged();
    
    if (event.dataTransfer?.files) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
    }
    input.value = ''; // Reset input to allow selecting same file again if they deleted it
  }

  removeExistingImage() {
    this.existingImageUrl = null;
    this.imageRemoved.emit();
    this.markStateChanged();
  }

  private async handleFiles(files: File[]) {
    if (!this.multiple && files.length > 1) {
      // If not multiple, just process the first one
      files = [files[0]];
    }

    // If not multiple, clear the existing processed files list first
    if (!this.multiple) {
      this.processedFiles = [];
    }

    const newProcessedFiles: ProcessedFile[] = files.map(file => ({
      file,
      status: 'NEW_PENDING',
      issues: [],
    }));

    this.processedFiles = [...this.processedFiles, ...newProcessedFiles];
    this.markStateChanged();
    
    const validResults: ValidImageResult[] = [];

    for (const item of newProcessedFiles) {
      const result = await validateImageFile(item.file, this.config);
      
      const fixableCodes = ['FILE_TOO_LARGE', 'ASPECT_RATIO_MISMATCH', 'DIMENSIONS_TOO_LARGE'];
      const hasFatal = result.issues.some(i => i.severity === 'BLOCKING' && !fixableCodes.includes(i.code));
      const hasFixable = result.issues.some(i => i.severity === 'BLOCKING' && fixableCodes.includes(i.code));
      const isFixable = !hasFatal && hasFixable;
      
      let nextStatus: ProcessedFileStatus = 'NEW_ACCEPTED';
      if (!result.isValid) {
        nextStatus = isFixable ? 'NEW_REJECTED_FIXABLE' : 'NEW_REJECTED_FATAL';
      }

      item.status = nextStatus;
      item.issues = result.issues;
      item.width = result.width;
      item.height = result.height;
      item.url = result.previewUrl;
      this.markStateChanged();
      
      if (result.isValid) {
        validResults.push({
          file: item.file,
          url: result.previewUrl,
          width: result.width,
          height: result.height,
        });
      }
    }

    if (validResults.length > 0) {
      this.filesSelected.emit(validResults);
      // Auto-clear accepted items explicitly on multiple=true so parent can absorb them
      if (this.multiple) {
        this.processedFiles = this.processedFiles.filter(pf => pf.status !== 'NEW_ACCEPTED');
      }
      this.markStateChanged();
    }
  }

  removeProcessedFile(pf: ProcessedFile) {
    this.processedFiles = this.processedFiles.filter(item => item !== pf);
    this.markStateChanged();
  }

  hasWarnings(pf: ProcessedFile): boolean {
    return pf.issues.some(i => i.severity === 'WARNING');
  }

  getBlockingIssues(pf: ProcessedFile): ValidationIssue[] {
    return pf.issues.filter(i => i.severity === 'BLOCKING');
  }

  getWarningIssues(pf: ProcessedFile): ValidationIssue[] {
    return pf.issues.filter(i => i.severity === 'WARNING');
  }

  async openEditor(pf: ProcessedFile, reasonOverride?: string) {
    try {
      this.activeFileMetadata = await readImageMetadata(pf.file);
      this.activeFileToEdit = pf;
      this.activeFileFixReason = reasonOverride || pf.issues[0]?.message || 'Image needs optimization.';
      this.markStateChanged();
    } catch (e) {
      console.error('Could not decode file for editing.', e);
    }
  }

  async onEditorSaved(newFile: File) {
    if (!this.activeFileToEdit) return;
    
    // Replace file
    this.activeFileToEdit.file = newFile;
    
    // Re-run validation on the exact object instance inside processedFiles array
    // so UI updates seamlessly
    const result = await validateImageFile(newFile, this.config);
    
    const fixableCodes = ['FILE_TOO_LARGE', 'ASPECT_RATIO_MISMATCH', 'DIMENSIONS_TOO_LARGE'];
    const hasFatal = result.issues.some(i => i.severity === 'BLOCKING' && !fixableCodes.includes(i.code));
    const hasFixable = result.issues.some(i => i.severity === 'BLOCKING' && fixableCodes.includes(i.code));
    const isFixable = !hasFatal && hasFixable;
    
    let nextStatus: ProcessedFileStatus = 'NEW_ACCEPTED';
    if (!result.isValid) {
      nextStatus = isFixable ? 'NEW_REJECTED_FIXABLE' : 'NEW_REJECTED_FATAL';
    }
    this.activeFileToEdit.status = nextStatus;
    this.activeFileToEdit.issues = result.issues;
    this.activeFileToEdit.width = result.width;
    this.activeFileToEdit.height = result.height;
    this.activeFileToEdit.url = result.previewUrl;
    
    if (result.isValid) {
      this.filesSelected.emit([{
        file: newFile,
        url: result.previewUrl,
        width: result.width,
        height: result.height,
      }]);
      if (this.multiple) {
        this.processedFiles = this.processedFiles.filter(pItem => pItem !== this.activeFileToEdit);
      }
    }
    
    this.closeEditor();
  }

  closeEditor() {
    this.activeFileToEdit = null;
    this.activeFileMetadata = null;
    this.markStateChanged();
  }
}
